<template>
  <div class="cmp">
    <!-- El periodo lo manda la barra de arriba (compartida con las otras sub-pestañas): aquí solo
         se muestra y se ofrece acotar por fecha de entrega. -->
    <div class="cmp-filtros">
      <div class="cmp-periodo">
        <div class="cmp-periodo-lbl">Periodo a comparar</div>
        <div class="cmp-periodo-val">
          <v-icon x-small class="mr-1">mdi-calendar-range</v-icon>
          <template v-if="inicio || fin">{{ fmtFecha(inicio) }} &rarr; {{ fmtFecha(fin) }}</template>
          <template v-else>—</template>
        </div>
        <div class="cmp-periodo-ayuda">
          Se ajusta con Inicio y Fin de la barra superior, o con «Semana en curso».
        </div>
      </div>

      <div class="cmp-entrega">
        <VueCtkDateTimePicker v-model="fechaEntrega" :disabled="descargando" only-date
          format="YYYY-MM-DD" formatted="YYYY-MM-DD" first-day-of-week="1" noHeader overlay
          label="Fecha de entrega (opcional)" class="cmp-fecha" />
        <div class="cmp-periodo-ayuda">
          Vacío: toda orden cuya fecha compromiso caiga en el periodo.
        </div>
      </div>

      <v-btn small text :disabled="descargando || !fechaEntrega" @click="fechaEntrega = null"
        title="Quitar el filtro de fecha de entrega">
        <v-icon left small>mdi-close</v-icon> Limpiar
      </v-btn>

      <v-spacer />

      <v-btn color="primary" :loading="descargando" :disabled="!inicio || !fin" @click="descargar">
        <v-icon left>mdi-file-excel-outline</v-icon> Generar y descargar
      </v-btn>
    </div>

    <v-alert v-if="error" type="error" dense text class="mx-3 mt-3 mb-0">{{ error }}</v-alert>
    <v-alert v-if="exito" type="success" dense text class="mx-3 mt-3 mb-0">{{ exito }}</v-alert>

    <!-- Qué trae el libro y con qué reglas se arma: el mismo texto que va dentro del Excel, para
         que no haya que abrirlo para saber qué se está comparando. -->
    <div class="cmp-info">
      <div class="cmp-col">
        <div class="cmp-tit"><v-icon small class="mr-1">mdi-file-table-outline</v-icon> Qué genera</div>
        <p>
          Un Excel que contrasta el <strong>CSV de la última carga de QAD</strong> contra las
          <strong>barras teóricas</strong> del tablero de producción V4 del mismo periodo, con cuatro
          hojas: <em>Resumen</em>, <em>Comparativo</em> (un bloque de tres filas por orden: QAD,
          ManagrX y Diferencia), <em>No válidas</em> y <em>Diferencias</em>.
        </p>
        <p class="cmp-nota">
          El resumen trae una fila de control contra <code>avance.piezasObjetivo</code> del tablero:
          si no da 0, hay barras que no empataron con ninguna orden del CSV.
        </p>
      </div>

      <div class="cmp-col">
        <div class="cmp-tit"><v-icon small class="mr-1">mdi-format-list-numbered</v-icon> Reglas</div>
        <ol class="cmp-reglas">
          <li>Estatus = <strong>R</strong></li>
          <li>Fecha compromiso: la indicada, o dentro del periodo</li>
          <li>Molde real: no vacío y distinto de <strong>TBD</strong></li>
          <li class="cmp-r45">Tiene fecha de arranque</li>
          <li class="cmp-r45">Su molde coincide con el que reporta ManagrX</li>
        </ol>
        <p class="cmp-nota">
          Las que cumplen 1&#8209;3 son <strong>registrables</strong> y forman la meta. Las que además
          cumplen 4&#8209;5 son <strong>válidas</strong>; las demás se marcan como dato malo en QAD,
          pero siguen contando en la meta porque ManagrX sí las programó.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { fmtFecha, filtroAIso, mensajeError } from './utils'

export default {
  name: 'CargaOtsComparativoExcel',

  data() {
    return {
      // Periodo vigente, recibido del contenedor en cargar().
      inicio: '',
      fin: '',
      // Opcional: acota el CSV a una sola fecha compromiso.
      fechaEntrega: null,
      descargando: false,
      error: '',
      exito: '',
    }
  },

  methods: {
    fmtFecha,

    // El contenedor llama a cargar() al abrir la pestaña y al pulsar "Buscar". Aquí no se
    // consulta nada: el Excel se arma solo cuando se pide, porque es una operación pesada.
    cargar(inicio, fin) {
      this.inicio = inicio || ''
      this.fin = fin || ''
      this.error = ''
      this.exito = ''
    },

    descargar() {
      if (this.descargando) return
      this.error = ''
      this.exito = ''

      const params = new URLSearchParams()
      if (this.inicio) params.append('inicio', this.inicio)
      if (this.fin) params.append('fin', this.fin)
      if (this.fechaEntrega) params.append('fechaEntrega', filtroAIso(this.fechaEntrega))

      this.descargando = true
      this.$emit('cargando', true)
      axios.get(`/CargaOts/ComparativoExcel?${params.toString()}`, { responseType: 'blob' })
        .then((response) => {
          const contentDisposition = response.headers && response.headers['content-disposition']
          const match = contentDisposition && contentDisposition.match(/filename="?([^";]+)"?/i)
          const nombreArchivo = match ? match[1] : 'ComparativoQAD-ManagrX.xlsx'
          const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          })
          const url = URL.createObjectURL(blob)
          const enlace = document.createElement('a')
          enlace.href = url
          enlace.download = nombreArchivo
          document.body.appendChild(enlace)
          enlace.click()
          document.body.removeChild(enlace)
          URL.revokeObjectURL(url)
          this.exito = `Se descargó ${nombreArchivo}.`
        })
        .catch(async (e) => {
          // Cuando no hay nada que comparar el back responde 404 con el motivo; al pedir el
          // archivo como blob hay que leerlo de vuelta para poder mostrarlo.
          this.error = await this.leerMensajeBlob(e)
        })
        .finally(() => {
          this.descargando = false
          this.$emit('cargando', false)
        })
    },

    async leerMensajeBlob(e) {
      const respaldo = 'No se pudo generar el comparativo.'
      const data = e && e.response && e.response.data
      if (data instanceof Blob) {
        try {
          const texto = await data.text()
          const json = JSON.parse(texto)
          return json.message || respaldo
        } catch (_) {
          return respaldo
        }
      }
      return mensajeError(e, respaldo)
    },
  },
}
</script>

<style scoped>
.cmp {
  padding-bottom: 12px;
}

.cmp-filtros {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  padding: 14px 16px 4px;
}

.cmp-periodo-lbl,
.cmp-tit {
  font-size: 12px;
  font-weight: 600;
  color: #37474f;
}

.cmp-periodo-val {
  font-size: 14px;
  font-weight: 600;
  color: #1f3864;
  margin-top: 2px;
}

.cmp-periodo-ayuda {
  font-size: 11px;
  color: #78909c;
  margin-top: 2px;
}

.cmp-fecha {
  width: 220px;
}

.cmp-info {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 14px 16px 0;
}

.cmp-col {
  flex: 1 1 320px;
  min-width: 280px;
}

.cmp-col p {
  font-size: 12px;
  color: #546e7a;
  margin: 6px 0 0;
  line-height: 1.5;
}

.cmp-nota {
  color: #78909c !important;
}

.cmp-reglas {
  font-size: 12px;
  color: #546e7a;
  margin: 6px 0 0;
  padding-left: 18px;
  line-height: 1.7;
}

.cmp-r45 {
  color: #9c0006;
}

code {
  background: #eceff1;
  box-shadow: none;
  font-size: 11px;
  padding: 1px 4px;
}
</style>
